import {
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  untracked,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { DetailsShellComponent } from '@mb/components';
import { OrgUnitSettingsNavigation } from './org-unit-settings-navigation';
import { canHoldConfiguration } from '../../../shared';
import type { OrgUnitSettingsTarget } from './org-chart.types';
import {
  resolveEntryPoint,
  UNIT_GROUP_ID,
  visibleGroups,
  type OrgUnitSettingsEntryPoint,
  type OrgUnitSettingsSectionId,
  type OrgUnitSettingsTopLevelId,
} from './org-unit-settings-sections';

/**
 * Per-OE configuration panel — what the gear icon on an OE card opens.
 *
 * One interaction, repeated at every depth: a list, a row, the next list. The
 * subject areas are a list, their sections are a list, the entries of a section
 * are a list, and an entry opens its form. Nothing branches into a different
 * shape halfway down, and the breadcrumb says where you are.
 *
 * Deliberately not a tab strip. Tabs put every section on one line and say
 * nothing about what belongs together; they also stop scaling the moment a
 * third subject area appears.
 *
 * Which section sits in which group is decided in `org-unit-settings-sections`,
 * not here; the host keeps passing plain section ids and needs no change.
 *
 * The section content is projected, so this panel never depends on the feature
 * modules that supply it. Each `ng-content` therefore appears exactly once and
 * the sections are written out rather than looped: projection is resolved when
 * the template is compiled, and a loop would ask for the same content twice.
 */
@Component({
  selector: 'org-unit-settings-panel',
  standalone: true,
  imports: [
    TranslocoPipe,
    DetailsShellComponent,
  ],
  template: `
    <app-details-shell>
      <nav
        detailsTrail
        class="flex flex-wrap items-center gap-1 text-sm"
        [attr.aria-label]="'organigram.panel.title.settings' | transloco"
      >
        @if (openGroup(); as group) {
          <button
            type="button"
            class="rounded text-muted-foreground transition hover:text-foreground hover:underline"
            (click)="showGroups()"
          >
            {{ 'organigram.panel.settings.root' | transloco }}
          </button>

          <span class="text-muted-foreground" aria-hidden="true">›</span>

          @if (openSection(); as section) {
            <button
              type="button"
              class="rounded text-muted-foreground transition hover:text-foreground hover:underline"
              (click)="showSections(group)"
            >
              {{ 'organigram.panel.settings.groups.' + group | transloco }}
            </button>
            <span class="text-muted-foreground" aria-hidden="true">›</span>

            @if (openItem(); as item) {
              <button
                type="button"
                class="rounded text-muted-foreground transition hover:text-foreground hover:underline"
                (click)="leaveItem()"
              >
                {{ 'organigram.panel.settings.' + section | transloco }}
              </button>
              <span class="text-muted-foreground" aria-hidden="true">›</span>
              <span class="font-medium text-foreground" aria-current="page">
                {{
                  item.labelKey ? (item.labelKey | transloco) : item.label
                }}
              </span>
            } @else {
              <span class="font-medium text-foreground" aria-current="page">
                {{ 'organigram.panel.settings.' + section | transloco }}
              </span>
            }
          } @else if (group === UNIT_GROUP_ID) {
            <span class="font-medium text-foreground" aria-current="page">
              {{ unit().name }}
            </span>
          } @else {
            <span class="font-medium text-foreground" aria-current="page">
              {{ 'organigram.panel.settings.groups.' + group | transloco }}
            </span>
          }
        } @else {
          <!-- The top of the drill-down: the trail is just where you are. -->
          <span class="font-medium text-foreground" aria-current="page">
            {{ 'organigram.panel.settings.root' | transloco }}
          </span>
        }
      </nav>

      <div class="pt-4">
        @if (openGroup() === null) {
          <ul class="divide-y divide-border rounded-md border border-border">
            <li>
              <button
                type="button"
                class="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-secondary"
                (click)="showUnit()"
              >
                <span class="min-w-0">
                  <span
                    class="block truncate text-sm font-medium text-foreground"
                  >
                    {{ unit().name }}
                  </span>
                  <span class="block truncate text-xs text-muted-foreground">
                    {{ 'organigram.panel.settings.groups.hints.unit' | transloco }}
                  </span>
                </span>
                <span class="shrink-0 text-muted-foreground" aria-hidden="true"
                  >›</span
                >
              </button>
            </li>
            @for (group of groups(); track group.id) {
              <li>
                <button
                  type="button"
                  class="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-secondary"
                  (click)="showSections(group.id)"
                >
                  <span class="min-w-0">
                    <span
                      class="block truncate text-sm font-medium text-foreground"
                    >
                      {{
                        'organigram.panel.settings.groups.' + group.id
                          | transloco
                      }}
                    </span>
                    <span
                      class="block truncate text-xs text-muted-foreground"
                    >
                      {{
                        'organigram.panel.settings.groups.hints.' + group.id
                          | transloco
                      }}
                    </span>
                  </span>
                  <span class="shrink-0 text-muted-foreground" aria-hidden="true"
                    >›</span
                  >
                </button>
              </li>
            }
          </ul>

          @if (inheritsConfiguration()) {
            <!--
              Without this the list holds one entry and nothing else, which
              reads as a permission problem rather than as the design.
            -->
            <p
              class="text-xs text-muted-foreground"
              data-testid="inherits-configuration"
            >
              {{ 'organigram.panel.settings.inherited' | transloco }}
            </p>
          }
        } @else if (openSection() === null && openGroup() !== UNIT_GROUP_ID) {
          <ul class="divide-y divide-border rounded-md border border-border">
            @for (section of sectionsOfOpenGroup(); track section) {
              <li>
                <button
                  type="button"
                  class="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-secondary"
                  (click)="showSection(section)"
                >
                  <span class="min-w-0">
                    <span
                      class="block truncate text-sm font-medium text-foreground"
                    >
                      {{
                        'organigram.panel.settings.' + section | transloco
                      }}
                    </span>
                    <span
                      class="block truncate text-xs text-muted-foreground"
                    >
                      {{
                        'organigram.panel.settings.hints.' + section
                          | transloco
                      }}
                    </span>
                  </span>
                  <span class="shrink-0 text-muted-foreground" aria-hidden="true"
                    >›</span
                  >
                </button>
              </li>
            }
          </ul>
        }

        @if (openGroup() === UNIT_GROUP_ID) {
          <ng-content select="[orgUnitSettingsUnit]" />
        }
        @if (openSection() === 'employments') {
          <ng-content select="[orgUnitSettingsEmployments]" />
        }
        @if (openSection() === 'costCenters') {
          <ng-content select="[orgUnitSettingsCostCenters]" />
        }
        @if (openSection() === 'bookingTypes') {
          <ng-content select="[orgUnitSettingsBookingTypes]" />
        }
        @if (openSection() === 'timeTypes') {
          <ng-content select="[orgUnitSettingsTimeTypes]" />
        }
        @if (openSection() === 'dayPrograms') {
          <ng-content select="[orgUnitSettingsDayPrograms]" />
        }
        @if (openSection() === 'workSchedules') {
          <ng-content select="[orgUnitSettingsWorkSchedules]" />
        }
        @if (openSection() === 'publicHolidays') {
          <ng-content select="[orgUnitSettingsPublicHolidays]" />
        }
        @if (openSection() === 'accounts') {
          <ng-content select="[orgUnitSettingsAccounts]" />
        }
        @if (openSection() === 'rules') {
          <ng-content select="[orgUnitSettingsRules]" />
        }
      </div>
    </app-details-shell>
  `,
})
export class OrgUnitSettingsPanelComponent {
  readonly unit = input.required<OrgUnitSettingsTarget>();
  /**
   * Which configuration sections the actor may see. The host decides — each is
   * gated by its own feature permission, while the gear itself only needs
   * `organigram.update`.
   */
  readonly availableTabs = input<readonly string[]>([]);
  /**
   * The section to open on, from wherever the caller last left off. `null`
   * opens the top-level group list.
   */
  readonly initialSection = input<string | null>(null);
  /** Emits whenever a section is opened or left, so a caller can remember it. */
  readonly sectionChanged = output<OrgUnitSettingsSectionId | null>();

  readonly #navigation = inject(OrgUnitSettingsNavigation);

  /**
   * The entry a section is showing a form for, if any.
   *
   * Read from the shared store rather than from an input: the sections reach
   * this panel through content projection, so there is nothing to bind to.
   */
  readonly openItem = this.#navigation.openItem;

  readonly groups = computed(() =>
    visibleGroups(this.availableTabs(), this.unit().type),
  );

  /**
   * True where configuration is inherited rather than maintained.
   *
   * Said out loud on the panel: an entry list holding nothing but the unit's
   * own record otherwise reads as a permission problem or a fault, when it is
   * the design.
   */
  readonly inheritsConfiguration = computed(
    () => !canHoldConfiguration(this.unit().type),
  );

  /**
   * Where the drill-down currently stands.
   *
   * Re-seeded when the panel is pointed at *another unit* — a different unit
   * may reach a different set of sections, and a stale position would show a
   * level it does not offer.
   *
   * Deliberately not re-seeded from `initialSection`. That input is fed by the
   * same store this panel writes to as you navigate, so tracking it would make
   * every step reset the position: walking back one level emits "no section",
   * which resolves to the top and threw you out of the group you were in.
   * `initialSection` is a starting point, read once per unit, not a binding.
   */
  readonly #position = linkedSignal<string, OrgUnitSettingsEntryPoint>({
    // The type is part of the key: retyping a company to a department from
    // inside this very panel takes its groups away, and a position pointing
    // into one of them would then show an empty level.
    source: () =>
      `${this.unit().id}|${this.unit().type}|${this.availableTabs().join(',')}`,
    computation: () =>
      untracked(
        () =>
          resolveEntryPoint(
            this.availableTabs(),
            this.initialSection(),
            this.unit().type,
          ) ?? { group: null, section: null },
      ),
  });

  /** Exposed for the template's comparisons against the unit entry. */
  protected readonly UNIT_GROUP_ID = UNIT_GROUP_ID;

  readonly openGroup = computed(() => this.#position().group);
  readonly openSection = computed(() => this.#position().section);

  readonly sectionsOfOpenGroup = computed<readonly OrgUnitSettingsSectionId[]>(
    () =>
      this.groups().find((group) => group.id === this.openGroup())?.sections ??
      [],
  );

  /** True while the panel is showing the unit's own form. */
  readonly editingUnit = computed(() => this.openGroup() === UNIT_GROUP_ID);

  showGroups(): void {
    this.#position.set({ group: null, section: null });
    this.sectionChanged.emit(null);
  }

  /** The unit's own record — a leaf on the top level, not a list of sections. */
  showUnit(): void {
    this.showSections(UNIT_GROUP_ID);
  }

  /**
   * Open a top-level entry: an area's section list, or the unit's own form.
   *
   * Takes the wider id because the breadcrumb hands back whatever `openGroup()`
   * holds. A section is only ever open under a real area — the unit has none —
   * so that crumb can only carry an area in practice; the type just cannot say
   * so, and narrowing it with a cast would be a worse answer than accepting
   * both.
   */
  showSections(group: OrgUnitSettingsTopLevelId): void {
    this.#position.set({ group, section: null });
    this.sectionChanged.emit(null);
  }

  showSection(section: OrgUnitSettingsSectionId): void {
    this.#position.set({ group: this.openGroup(), section });
    this.sectionChanged.emit(section);
  }

  /** Breadcrumb: back from an entry's form to the list it came from. */
  leaveItem(): void {
    this.#navigation.leaveItem();
  }
}
